import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import {
  MessageSquareText,
  LayoutDashboard,
  Settings as SettingsIcon,
  Search,
  Bell,
  Table as TableIcon,
  Gauge,
  Sparkles as SparklesIcon,
  BotMessageSquare,
  MousePointerClick,
  ClipboardList,
  Server,
  LogOut,
  Menu,
  X,
  Wallet,
  Eye,
  Recycle,
  Mail as MailIcon,
  Package,
  Network,
  ChevronDown,
  KeyRound,
  CalendarClock,
  Activity,
  BookOpen,
} from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { cn } from "@/lib/utils";
import { getViewAsRole, setViewAsRole } from "@/lib/api";
import { logout, useCurrentUser } from "@/lib/auth";
import GlobalSearch from "@/components/GlobalSearch";
import { PhaseStatusPills } from "@/components/PhaseStatusPills";
import ChatWidget from "@/components/ChatWidget";
import { canSee, type PageKey } from "@/lib/pages";
import {
  ANALYSIS_TABS,
  MONITORING_TABS,
  OFFERS_TABS,
  SETTINGS_TABS,
  SITES_TABS,
  type PageTab,
} from "@/components/PageTabs";

type NavItem = {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  // Страница роли. У admin-only пунктов (page отсутствует, adminOnly=true)
  // доступ не раздаётся чекбоксом — см. DevGuard.
  page?: PageKey;
  adminOnly?: boolean;
  // Только разработчик (DEVELOPER_EMAILS) — см. DevGuard.
  devOnly?: boolean;
  // Виден любому авторизованному юзеру (без ключа роли).
  everyone?: boolean;
  // Пункт меню, накрывающий несколько роутов-вкладок (см. PageTabs): виден,
  // если роль пускает хоть на одну вкладку; ведёт на первую доступную и
  // подсвечивается на любой из них.
  tabs?: PageTab[];
};

// Sidebar nav grouped into titled sections. The first group has no title (the
// home/overview link sits on its own at the top).
const NAV_GROUPS: { title?: string; items: NavItem[] }[] = [
  {
    items: [
      { to: "/", label: "Обзор", icon: LayoutDashboard, page: "dashboard" },
    ],
  },
  {
    title: "Статистика",
    items: [
      {
        to: "/sites",
        label: "Сайты",
        icon: TableIcon,
        page: "sites",
        tabs: SITES_TABS,
      },
      {
        to: "/ai-visibility",
        label: "ИИ-видимость",
        icon: BotMessageSquare,
        page: "ai-visibility",
      },
      {
        to: "/land-metrics",
        label: "Поведение на лендах",
        icon: MousePointerClick,
        page: "land-metrics",
      },
      {
        to: "/renewals",
        label: "Продления доменов",
        icon: CalendarClock,
        page: "renewals",
      },
    ],
  },
  {
    title: "Мониторинг",
    items: [
      {
        to: "/health",
        label: "Мониторинг",
        icon: Gauge,
        page: "health",
        tabs: MONITORING_TABS,
      },
      {
        to: "/reviews",
        label: "Отзывы",
        icon: MessageSquareText,
        page: "reviews",
      },
      { to: "/events", label: "События", icon: Bell, page: "events" },
    ],
  },
  {
    title: "Регистрации",
    items: [
      { to: "/regs", label: "Регистрации", icon: ClipboardList, page: "regs" },
      { to: "/networks", label: "Сети", icon: Network, page: "regs" },
      {
        to: "/recycling",
        label: "Ресайклинг",
        icon: Recycle,
        page: "recycling",
      },
      {
        to: "/offers",
        label: "Офферы",
        icon: Package,
        page: "offers",
        tabs: OFFERS_TABS,
      },
      { to: "/mailings", label: "Рассылки", icon: MailIcon, page: "mailings" },
      { to: "/finance", label: "Финансы", icon: Wallet, page: "finance" },
    ],
  },
  {
    title: "Сервер",
    items: [{ to: "/server", label: "Нагрузка", icon: Server, page: "server" }],
  },
  {
    title: "Аналитика",
    items: [
      {
        to: "/analysis",
        label: "AI-аналитика",
        icon: SparklesIcon,
        page: "analysis",
        tabs: ANALYSIS_TABS,
      },
    ],
  },
  {
    title: "Знания",
    items: [{ to: "/wiki", label: "Вики", icon: BookOpen, everyone: true }],
  },
  {
    title: "Настройки",
    items: [
      {
        to: "/settings",
        label: "Настройки",
        icon: SettingsIcon,
        page: "settings",
        tabs: SETTINGS_TABS,
      },
      {
        to: "/access",
        label: "Доступы",
        icon: KeyRound,
        devOnly: true,
      },
      {
        to: "/dev/usage",
        label: "Использование",
        icon: Activity,
        devOnly: true,
      },
    ],
  },
];

// Свёрнутые группы сайдбара переживают перезагрузку (по названию группы).
const NAV_COLLAPSED_KEY = "navCollapsedGroups";

// Grouped nav links, shared by the desktop sidebar and the mobile drawer.
// Открыта ли сейчас одна из вкладок пункта (подсветка «Сайты» на /sweep и т.п.).
function onTab(pathname: string, tabs?: PageTab[]): boolean {
  return (
    tabs?.some((t) => pathname === t.to || pathname.startsWith(t.to + "/")) ??
    false
  );
}

function NavSections({
  onNavigate,
  className,
}: {
  onNavigate?: () => void;
  className?: string;
}) {
  const { user } = useCurrentUser();
  const { pathname } = useLocation();
  const [stored, setCollapsed] = useLocalStorage<string[]>(
    NAV_COLLAPSED_KEY,
    [],
  );
  // Битое значение в хранилище (не массив строк) — считаем, что ничего не свёрнуто.
  const collapsed = Array.isArray(stored)
    ? stored.filter((x): x is string => typeof x === "string")
    : [];
  const toggleGroup = (title: string) =>
    setCollapsed(
      collapsed.includes(title)
        ? collapsed.filter((t) => t !== title)
        : [...collapsed, title],
    );
  // Пункт с вкладками: видим, если открыта хоть одна вкладка; ведём на первую
  // доступную (иначе роль без «Здоровья», но с «Краулингом», упиралась бы в
  // 403 при клике по «Мониторингу»).
  const groups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.flatMap((it) => {
      if (it.devOnly) return user?.isDeveloper ? [it] : [];
      if (it.adminOnly) return user?.isAdmin ? [it] : [];
      if (it.everyone) return user ? [it] : [];
      if (!it.page) return [];
      if (!it.tabs) return canSee(user, it.page) ? [it] : [];
      const open = it.tabs.filter((t) => canSee(user, t.page));
      return open.length ? [{ ...it, to: open[0].to }] : [];
    }),
  })).filter((group) => group.items.length > 0);

  return (
    <nav className={cn("flex flex-col", className)}>
      {groups.map((group, gi) => {
        const isCollapsed = group.title
          ? collapsed.includes(group.title)
          : false;
        return (
          <div key={group.title ?? `g${gi}`} className="flex flex-col gap-0.5">
            {group.title && (
              <button
                type="button"
                onClick={() => toggleGroup(group.title!)}
                aria-expanded={!isCollapsed}
                className="flex w-full items-center justify-between px-2.5 pb-0.5 pt-2.5 text-[10px] font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-ink-600 dark:text-ink-500 dark:hover:text-ink-300"
              >
                {group.title}
                <ChevronDown
                  className={cn(
                    "h-3 w-3 transition-transform",
                    isCollapsed && "-rotate-90",
                  )}
                />
              </button>
            )}
            {!isCollapsed &&
              group.items.map(({ to, label, icon: Icon, tabs }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/"}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors",
                      isActive || onTab(pathname, tabs)
                        ? "bg-brand-600/10 text-brand-700 dark:text-brand-300"
                        : "text-ink-600 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-ink-800/60",
                    )
                  }
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </NavLink>
              ))}
          </div>
        );
      })}
    </nav>
  );
}

export default function Shell({ children }: { children: React.ReactNode }) {
  // Тему по-прежнему применяем (side-effect хука); переключатель убран из шапки —
  // менять можно в «Настройках».
  useTheme();
  const { user } = useCurrentUser();
  // Активный предпросмотр «от лица роли» (вкл/выкл делает полный reload).
  const viewAs = getViewAsRole();
  // Мобильная навигация (drawer), сайдбар скрыт на узких экранах.
  const [mobileNav, setMobileNav] = useState(false);

  // Глобальный поиск: открывается по клику в инпут или по Ctrl/Cmd+K.
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // e.code — физическая клавиша: работает и в кириллической раскладке,
      // где e.key для K — «л».
      // defaultPrevented — Ctrl+K уже обработал редактор (вставка ссылки).
      if (e.defaultPrevented) return;
      if ((e.ctrlKey || e.metaKey) && e.code === "KeyK") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="bg-app min-h-full">
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <aside className="hidden md:flex w-52 shrink-0 flex-col gap-1 border-r border-ink-200/70 dark:border-ink-800/70 bg-white/60 dark:bg-ink-900/40 backdrop-blur-md px-3 py-4">
          <Link
            to="/"
            className="flex items-center gap-2 px-2 py-1.5 mb-2 group"
          >
            <div className="h-8 w-8 rounded-xl bg-brand-600 grid place-items-center text-white font-bold text-sm">
              SO
            </div>
            <div className="font-bold tracking-tight">Site Observer</div>
          </Link>

          <NavSections className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1" />
        </aside>

        {/* Main */}
        <div className="flex-1 min-w-0 overflow-x-clip">
          {/* Баннер админского предпросмотра «от лица роли» — всегда виден,
              даже когда страница «Пользователи» недоступна этой роли. */}
          {viewAs && (
            <div className="flex items-center gap-2 border-b border-amber-500/30 bg-amber-500/15 px-4 md:px-8 py-1.5 text-sm text-amber-700 dark:text-amber-300">
              <Eye className="h-4 w-4 shrink-0" />
              <span className="truncate">
                Просмотр от лица роли «{viewAs.name}»
              </span>
              <button
                type="button"
                className="btn ml-auto shrink-0 px-2 py-0.5 text-xs"
                onClick={() => {
                  setViewAsRole(null);
                  // BASE_URL, не "/": иначе деплой в подпапку выкидывает из приложения.
                  window.location.href = import.meta.env.BASE_URL || "/";
                }}
              >
                Выйти из просмотра
              </button>
            </div>
          )}
          <header className="sticky top-0 z-30 border-b border-ink-200/70 dark:border-ink-800/70 bg-white/70 dark:bg-ink-950/70 backdrop-blur-xl">
            <div className="flex items-center gap-2 md:gap-3 px-4 md:px-8 h-14 min-w-0">
              <button
                type="button"
                onClick={() => setMobileNav(true)}
                className="btn md:hidden shrink-0 px-2"
                title="Меню"
                aria-label="Меню"
              >
                <Menu className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                className="relative flex-1 min-w-0 max-w-xl text-left input pl-9 flex items-center gap-2 hover:border-brand-300 transition-colors"
              >
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                <span className="text-ink-400 truncate">
                  Поиск по сайтам, страницам, запросам, странам…
                </span>
                <kbd className="ml-auto text-[10px] text-ink-400 border border-ink-200 dark:border-ink-700 rounded px-1.5 py-0.5">
                  ⌘K
                </kbd>
              </button>
              <PhaseStatusPills />
              {user && (
                <div className="ml-auto flex items-center gap-2">
                  {user.picture ? (
                    <span className="block h-7 w-7 shrink-0 overflow-hidden rounded-full border border-ink-200 dark:border-ink-700">
                      <img
                        src={user.picture}
                        alt=""
                        referrerPolicy="no-referrer"
                        className="block h-full w-full object-cover"
                      />
                    </span>
                  ) : (
                    <div className="h-7 w-7 rounded-full bg-brand-600/10 text-brand-700 dark:text-brand-300 grid place-items-center text-xs font-semibold">
                      {user.email.slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <span className="hidden sm:inline text-sm text-ink-600 dark:text-ink-300 truncate max-w-[12rem]">
                    {user.email}
                  </span>
                  <button
                    type="button"
                    className="btn"
                    title="Выйти"
                    aria-label="Выйти"
                    onClick={() => logout()}
                  >
                    <LogOut className="h-4 w-4" />
                    <span className="hidden sm:inline">Выйти</span>
                  </button>
                </div>
              )}
            </div>
          </header>
          {/* overflow-x-clip stops stray wide children from creating a
              page-level horizontal scroll, without turning this into a scroll
              container (which would break sticky toolbars). */}
          <main className="px-4 md:px-8 pt-6 pb-24 animate-fade-in overflow-x-clip">
            {children}
          </main>
        </div>
      </div>

      {/* Mobile nav drawer */}
      {mobileNav && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Меню"
        >
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileNav(false)}
          />
          <aside className="absolute left-0 top-0 h-full w-64 max-w-[80%] bg-white dark:bg-ink-900 border-r border-ink-200/70 dark:border-ink-800/70 p-3 flex flex-col gap-1 overflow-auto animate-slide-up">
            <div className="flex items-center justify-between px-2 py-2 mb-2">
              <div className="font-bold tracking-tight">Site Observer</div>
              <button
                type="button"
                className="btn px-2"
                onClick={() => setMobileNav(false)}
                aria-label="Закрыть"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <NavSections onNavigate={() => setMobileNav(false)} />
          </aside>
        </div>
      )}

      <GlobalSearch
        open={searchOpen}
        query={searchQuery}
        onQueryChange={setSearchQuery}
        onClose={() => setSearchOpen(false)}
      />

      {canSee(user, "analysis") && <ChatWidget />}
    </div>
  );
}
